/**
 * participants slice: 원격 참여자 관리
 * wehago-conference의 redux/modules/participants 에서 리팩토링
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Participant, ParticipantTrackInfo } from '@/types/store';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

// re-export for backwards compatibility
export type { Participant, ParticipantTrackInfo } from '@/types/store';

export interface ParticipantsState {
  participants: Record<string, Participant>;
  // 순서 보장을 위한 ID 배열 (입장 순서)
  participantIds: string[];
  // 행위자(나)의 클라이언트에서만 음소거한 참여자 ID 목록 (v1 isMuteSpeaker 패턴)
  localMutedIds: string[];
}

const initialState: ParticipantsState = {
  participants: {},
  participantIds: [],
  localMutedIds: [],
};

const normalizeProfileUrl = (url?: string | null) => toAbsoluteProfileUrl(url) ?? '';

const normalizeParticipantProfileFields = <T extends { avatarUrl?: string; profileUrl?: string }>(payload: T): T => {
  const next = { ...payload };
  if ('profileUrl' in payload) {
    next.profileUrl = normalizeProfileUrl(payload.profileUrl);
  }
  return next;
};

const participantsSlice = createSlice({
  name: 'participants',
  initialState,
  reducers: {
    participantJoined(state, action: PayloadAction<Omit<Participant, 'tracks' | 'properties'>>) {
      const payload = normalizeParticipantProfileFields(action.payload);
      const { id } = payload;
      // TRACK_ADDED가 USER_JOINED보다 먼저 올 수 있어 기존 트랙 상태를 보존
      const existingTracks = state.participants[id]?.tracks ?? {
        hasAudio: false,
        hasVideo: false,
        audioMuted: true,
        videoMuted: true,
        isScreenSharing: false,
      };
      const existingProps = state.participants[id]?.properties ?? {};
      state.participants[id] = {
        ...payload,
        nickname: payload.nickname || state.participants[id]?.nickname || '',
        tracks: existingTracks,
        connectionQuality: 100,
        properties: existingProps,
      };
      if (!state.participantIds.includes(id)) {
        state.participantIds.push(id);
      }
    },

    participantLeft(state, action: PayloadAction<string>) {
      const id = action.payload;
      delete state.participants[id];
      state.participantIds = state.participantIds.filter((pid) => pid !== id);
    },

    participantUpdated(
      state,
      action: PayloadAction<Partial<Participant> & { id: string }>
    ) {
      const { id, ...updates } = action.payload;
      if (state.participants[id]) {
        Object.assign(state.participants[id], normalizeParticipantProfileFields(updates));
      }
    },

    participantTrackUpdated(
      state,
      action: PayloadAction<{ id: string; tracks: Partial<ParticipantTrackInfo> }>
    ) {
      const { id, tracks } = action.payload;
      // TRACK_ADDED가 USER_JOINED보다 먼저 올 수 있으므로 트랙 데이터 보존용 플레이스홀더 생성
      // 단, participantIds에는 추가하지 않음 — participantJoined에서만 UI 목록에 추가
      // (hidden 참여자가 TRACK만으로 UI에 노출되는 버그 방지)
      if (!state.participants[id]) {
        state.participants[id] = {
          id,
          displayName: '',
          nickname: '',
          wehagoId: '',
          companyCode: '',
          avatarUrl: '',
          selectedAvatar: '',
          nationality: '',
          role: 'none',
          isModerator: false,
          isHidden: false,
          userIdentificationCode: '',
          profileUrl: '',
          companyFullpath: '',
          isExternalParticipant: false,
          userName: '',
          avatar: '',
          externalUserId: '',
          userContact: '',
          userEmail: '',
          isScreen: false,
          userNo: 0,
          userType: '',
          host: false,
          isTranslationMode: '',
          enableAutoLang: 'false',
          voiceNoteLocale: '',
          employeeNo: '',
          companyNo: '',
          tracks: { hasAudio: false, hasVideo: false, audioMuted: true, videoMuted: true, isScreenSharing: false },
          connectionStatus: 'active',
          connectionQuality: 100,
          properties: {},
        };
      }
      Object.assign(state.participants[id].tracks, tracks);
    },

    participantPropertyChanged(
      state,
      action: PayloadAction<{ id: string; name: string; value: unknown }>
    ) {
      const { id, name, value } = action.payload;
      if (state.participants[id]) {
        const normalizedValue = (name === 'profileUrl' || name === 'profile_url')
          ? normalizeProfileUrl(String(value ?? ''))
          : value;
        state.participants[id].properties[name] = normalizedValue;
        if (name === 'avatarUrl') {
          state.participants[id].avatarUrl = String(value ?? '');
        }
        if (name === 'profileUrl' || name === 'profile_url') {
          state.participants[id].profileUrl = String(normalizedValue);
        }
        if (name === 'enableAutoLang') {
          state.participants[id].enableAutoLang = String(value ?? 'false');
        }
      }
    },

    participantConnectionQualityUpdated(
      state,
      action: PayloadAction<{ id: string; connectionQuality: number }>
    ) {
      const { id, connectionQuality } = action.payload;
      if (state.participants[id]) {
        state.participants[id].connectionQuality = connectionQuality;
      }
    },

    /**
     * 화이트보드 가짜 참여자 추가 (/video-renewal 전용).
     * 화이트보드 세션이 시작되면 WHITEBOARD_ID를 participantIds에 삽입하여
     * 모든 Screen 컴포넌트가 WhiteboardContainer를 렌더링하도록 트리거한다.
     */
    addPseudoParticipant(
      state,
      action: PayloadAction<{ id: string; name: string; isWhiteboard: true; ownerId?: string }>
    ) {
      const { id, name, isWhiteboard, ownerId } = action.payload;
      if (!state.participantIds.includes(id)) {
        // 소유자 바로 다음에 삽입
        if (ownerId) {
          const ownerIdx = state.participantIds.indexOf(ownerId);
          if (ownerIdx !== -1) {
            // 원격 소유자가 목록에 있으면 바로 다음에 삽입
            state.participantIds.splice(ownerIdx + 1, 0, id);
          } else {
            // 로컬(본인)이 소유자 → participantIds에 없으므로 맨 앞에 삽입
            // (UI에서 로컬은 별도 처리되어 항상 첫 번째, 그 다음이 participantIds[0])
            state.participantIds.unshift(id);
          }
        } else {
          state.participantIds.push(id);
        }
      }
      state.participants[id] = {
        id,
        displayName: name,
        nickname: name,
        wehagoId: '',
        companyCode: '',
        nationality: '',
        role: 'none',
        isModerator: false,
        isHidden: false,
        isWhiteboard,
        userIdentificationCode: '',
        profileUrl: '',
        companyFullpath: '',
        isExternalParticipant: false,
        userName: '',
        avatar: '',
        externalUserId: '',
        userContact: '',
        userEmail: '',
        isScreen: false,
        userNo: 0,
        userType: '',
        host: false,
        isTranslationMode: '',
        enableAutoLang: 'false',
        voiceNoteLocale: '',
        employeeNo: '',
        companyNo: '',
        tracks: {
          hasAudio: false,
          hasVideo: false,
          audioMuted: true,
          videoMuted: true,
          isScreenSharing: false,
        },
        connectionStatus: 'active',
        connectionQuality: 100,
        properties: {},
      };
    },

    /**
     * 화이트보드 가짜 참여자 제거 (/video-renewal 전용).
     * participantLeft와 동일하지만 명시적으로 가짜 참여자 전용.
     */
    removePseudoParticipant(state, action: PayloadAction<string>) {
      const id = action.payload;
      delete state.participants[id];
      state.participantIds = state.participantIds.filter((pid) => pid !== id);
    },

    /** 로컬 음소거 토글 (행위자한테만 음소거 — v1 toggleMuteSpeaker) */
    toggleLocalMuted(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.localMutedIds.indexOf(id);
      if (idx >= 0) {
        state.localMutedIds = state.localMutedIds.filter((v) => v !== id);
      } else {
        state.localMutedIds = [...state.localMutedIds, id];
      }
    },

    reset() {
      return initialState;
    },
  },
});

export const participantsActions = participantsSlice.actions;
export default participantsSlice.reducer;
